/* eslint-disable no-console */
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { Box, Grid, useMediaQuery, useTheme } from '@mui/material';
import { LanguageContext } from '../../../../../libs/Context';
import { createFeedContext } from '../../LeftPart/FeedCreation/CreationPopup';
import FeedAvatarList from '../../../../../components/Common/Feed/FeedAvatarList';
import GifTemplateContent from './Components/GifTemplateContent';
import ImgTemplateContent from './Components/ImgTemplateContent';
import { DesignElement, getDesignElements, planeCard } from '../EventCardUtils';

// Helper function to map employees to avatar list
const mapEmployeesToAvatars = (employees?: any[]) =>
{
	return Array.isArray(employees)
		? employees.map((each) => ({
			key : each?.userId + each?.name,
			src : each?.userId
				? `${process.env.REACT_APP_S3_BASE_URL}/profile/${each?.userId}_profile.jpeg`
				: '/images/icons/defaultUser.svg',
			name   : each?.name,
			userId : each?.userId
		}))
		: [];
};

const OccasionCardView = (props: any) =>
{
	const { selectEvent, occasionData, viewData,
		requestT, elementRef, taggedUserRef } = props;
	const theme = useTheme();
	const downSM = useMediaQuery(theme.breakpoints.down('sm'));
	const { language } = useContext(LanguageContext);
	const { setCreateFeed, createFeed } = useContext(createFeedContext);
	const upMD = useMediaQuery(theme.breakpoints.up(700));

	// State management
	const [ textState, setTextState ] = useState({
		title : occasionData?.title ?? '',
		text  : occasionData?.text ?? ''
	});
	const [ openUserList, setOpenUserList ] = useState(false);

	// Handlers
	const handleAttendees = () => setOpenUserList(true);
	const handleAttendeesClose = () => setOpenUserList(false);

	// Avatar list memoization
	const avatarList = React.useMemo(() =>
		mapEmployeesToAvatars(occasionData?.employees ?? viewData?.occasion?.employees),
	[ occasionData?.employees, viewData?.occasion?.employees ]);

	// Update occasion card
	const updateOccasionCard = useCallback(async (updObj) =>
	{
		const updatedTextValue = { ...updObj };

		if (occasionData)
		{
			taggedUserRef.current = [ ...(occasionData?.employees || []) ];
			setCreateFeed((prev: any) => ({
				...prev,
				occasion : {
					...occasionData, // Spread existing occasionData
					title : updatedTextValue?.title,
					text  : updatedTextValue?.text
				},
				capturedDOM : elementRef.current ?? []
			}));
		}
	}, [ occasionData, textState ]);

	// Text change handler
	const handleChange = (textVal: any, textTitle: any) =>
	{
		if ([ 'title', 'text' ].includes(textTitle))
		{
			setTextState((prev) => ({
				...prev,
				[textTitle] : textVal
			}));
			updateOccasionCard({ ...textState, [textTitle]: textVal });
		}
	};

	// Remove tagged user
	const handleRemove = (userId: any) =>
	{
		const updatedEmployees = occasionData?.employees?.filter((user: any) =>
			user?.userId !== userId) || [];

		setCreateFeed((prev: any) => ({
			...prev,
			occasion : {
				...prev?.occasion,
				employees : updatedEmployees
			}
		}));

		taggedUserRef.current = updatedEmployees;
	};

	// Determine template type
	const hasGifTemplate = (occasionData?.templateUrl || viewData?.occasion?.templateUrl || '').endsWith('.gif');

	// Get template card if it exists
	const templateKey = occasionData?.templateUrlWeb || viewData?.occasion?.templateUrlWeb;
	const templateCard = planeCard.find((card) => card.key === templateKey);

	useEffect(() =>
	{
		// for updating emoji's from feed footer to this component #I12582
		// emoji is being set on footer component only
		createFeed?.occasion?.text && setTextState((prev) => ({
			...prev,
			text : createFeed?.occasion?.text
		}));
	}, [ createFeed?.occasion?.text ]);

	return (
		<Grid item xs={12}>
			<Box
				ref={elementRef}
				alignItems='center'
				justifyContent='center'
				display='flex'
				sx={{
					borderRadius : '8px',
					width        : requestT === 'viewEvent' ? '100%' : hasGifTemplate ? '100%' : '60%',
					height       : 'auto',
					position     : 'relative',
					overflow     : 'hidden'
				}}
			>
				{templateKey && (
					templateCard
						? (
							<Box
								alignItems='center'
								display='flex'
								sx={{
									width        : '100%',
									borderRadius : '8px',
									height       : '100%',
									objectFit    : 'cover',
									overflow     : 'hidden',
									aspectRatio  : requestT === 'viewEvent' ? '16/7' : hasGifTemplate ? '16/7' : '16/8',
									background   : templateCard.src,
									position     : 'relative'
								}}
							>
								{getDesignElements(
									templateCard.eventType,
									requestT,
									upMD
								).map((item, idx) => (
									<DesignElement key={idx} item={item} />
								))}
							</Box>
						)
						: (
							<Box
								component='img'
								src={templateKey}
								alt='Uploaded Template'
								sx={{
									width        : '100%',
									borderRadius : '8px',
									height       : '100%',
									objectFit    : 'cover',
									aspectRatio  : requestT === 'viewEvent' ? '16/7' : hasGifTemplate ? '16/7' : '16/8'
								}}
							/>
						)
				)}

				<Box
					sx={{
						position : 'absolute',
						width    : '100%',
						height   : '100%',
						display  : 'flex'
					}}
				>
					{hasGifTemplate
						? (
							<GifTemplateContent
								downSM={downSM}
								requestT={requestT}
								viewData={viewData}
								textState={textState}
								occasionData={occasionData}
								handleAttendees={handleAttendees}
								handleChange={handleChange}
								avatarList={avatarList}
								selectEvent={selectEvent}
								handleRemove={handleRemove}
								hasGifTemplate={hasGifTemplate}
							/>
						)
						: (
							<ImgTemplateContent
								downSM={downSM}
								requestT={requestT}
								viewData={viewData}
								textState={textState}
								occasionData={occasionData}
								handleAttendees={handleAttendees}
								avatarList={avatarList}
								handleChange={handleChange}
								hasGifTemplate={hasGifTemplate}
							/>
						)}
				</Box>
			</Box>

			{/* User list popup */}
			{openUserList && (
				<FeedAvatarList
					openPopUp={openUserList}
					title={`${language?.MyTags} ${language?.people}`}
					handleAttendeesClose={handleAttendeesClose}
					requestType='occasion'
					getFeedData={occasionData ?? viewData?.occasion}
				/>
			)}
		</Grid>
	);
};

export default OccasionCardView;